import React, {useEffect, useState, useContext} from "react";
import {Checkbox, Divider} from 'antd';
import axios from "../common/axios";
import {UserContext} from "../common/UserContext";
import Tabs from "../common/Tabs";
import Tab from "../common/Tab";
import Button from "../common/Button";
import GenericDialog from "../common/GenericDialog";
import {Inbox, Outbox} from "@mui/icons-material";
import TableAntD from "../common/TableAntD";
import PopConfirm from "../common/PopConfirm";
import {isCancel} from "axios";
import ButtonsGroup from "../common/ButtonsGroup";
import ComposeMessageDialog from "./ComposeMessageDialog";

const Messages = props => {
    const {user} = useContext(UserContext);
    const [isDeleteAllDisabled, setDeleteAllDisabled] = useState(true);
    const [currentMessage, setCurrentMessage] = useState(null);
    const [messages, setMessages] = useState([]);
    const [isMarkCurrentMessageRead, setMarkCurrentMessageRead] = useState(false);
    const [selectedMessageKeys, setSelectedMessageKeys] = useState([]);
    const [isComposeDialogOpen, setComposeDialogOpen] = useState(false);
    const [isReadMessageDialogOpen, setReadMessageDialogOpen] = useState(false);
    const [reload, setReload] = useState(null);

    const TABS = {
        INBOX: {
            key: 'INBOX', value: 'INBOX', name: 'Inbox', hash: 'inbox', cols: [
                {title: <Checkbox
                        aria-label={'select all messages'}
                        checked={messages.length > 0 && selectedMessageKeys.length === messages.length}
                        onChange={(e) => onSelectAllRows(e.target.checked)}
                    ><span className={'ch-accessibility-label'}>select all messages</span></Checkbox>,
                    dataIndex: 'checkbox',
                    width: 30,
                    render: (_, message) => (
                        <Checkbox
                            aria-label={`select message ${message.subject}`}
                            checked={selectedMessageKeys.includes(message.key)}
                            onChange={e => onSelectRow(e.target.checked, message.key)}
                        ><span className={'ch-accessibility-label'}>select message</span></Checkbox>
                    )
                },
                {title: 'From', dataIndex: 'from'},
                {title: 'Status', dataIndex: 'status'},
                {title: 'Subject', dataIndex: 'subject'},
                {title: 'Sent', dataIndex: 'sent'},
                {title: 'Expiry', dataIndex: 'expiry'},
                {title: 'View', dataIndex: 'view'}
            ]
        },
        SENT: {
            key: 'SENT_ITEMS', value: 'SENT_ITEMS', name: 'Sent Items', hash: 'sent', cols: [
                {title: 'To', dataIndex: 'to'},
                {title: 'Subject', dataIndex: 'subject'},
                {title: 'Sent', dataIndex: 'sent'},
                {title: 'Expiry', dataIndex: 'expiry'},
                {title: 'View', dataIndex: 'view'}
            ]
        }
    };

    useEffect(() => {
        setReadMessageDialogOpen(currentMessage !== null);
        if (currentMessage && !currentMessage.read) {
            axios.post('/web/ui/message/markUserMessageRead', null, {params: {messageId: currentMessage.key}})
                .then(() => {
                    setMarkCurrentMessageRead(true);
                }).catch(error => {
                console.error('error marking message as read', error);
            });
        }
    }, [currentMessage]);

    const updateReload = (doReload) => {
        setCurrentMessage(null);
        if (isMarkCurrentMessageRead || doReload) {
            setReload(new Date().getTime());
            setMarkCurrentMessageRead(false);
        }
    };

    const getUserMessages = (params, handleTableData, handlePagingData, abortController) => {
        axios.get('/web/ui/message/userMessagesInbox', {params: params, signal: abortController.signal})
            .then(response => {
                if (response && response.data) {
                    let dataSource = [];

                    response.data.currPageContents.map((message) => {
                        const messageTemp = {
                            key: message.messageId,
                            to: message.recipientName,
                            from: message.senderName,
                            status: message.read ? 'Read' : 'Unread',
                            subject: message.subject,
                            sent: user.dateFormat.format(message.createDate),
                            expiry: user.dateFormat.format(message.expiryDate),
                            read: message.read,
                            body: message.messageBody
                        };

                        messageTemp.view = <Button onClick={() => handleViewMessage(messageTemp)} aria-label={`View message ${message.subject}`}>View</Button>;

                        dataSource.push(messageTemp);
                    });

                    setMessages(dataSource);

                    handleTableData(dataSource);
                    handlePagingData({pageSize: params.pageSize, totalResults: response.data.totalResults, currPage: params.pageNumber});
                    setDeleteAllDisabled(response.data.totalResults == 0);
                }
            })
            .catch((error) => {
                if (!isCancel(error)) {
                    console.error('Error retrieving messages: ', error);
                    displayToastMessage('Error retrieving messages:', error, 'error');
                }
            })
            .finally(() => {
                setSelectedMessageKeys([]);
                abortController = null;
            });
    };

    const deleteSelectedMessages = () => {
        axios.post('/web/ui/message/deleteUserMessages', selectedMessageKeys)
            .then(response => {
                if (response.data) {
                    updateReload(true);
                    displayToastMessage('Messages', 'Selected messages deleted successfully', 'success', undefined, 5000);
                }
            }).catch(error => {
            console.error('error deleting messages', error);
        });
    };

    const deleteAllMessages = () => {
        axios.post('/web/ui/message/deleteAllUserMessages')
            .then(response => {
                if (response.data) {
                    updateReload(true);
                    displayToastMessage('Messages', 'All messages deleted successfully', 'success', undefined, 5000);
                }
            }).catch(error => {
            console.error('error deleting messages', error);
        });
    };

    const handleTabChange = (key) => {
        setSelectedMessageKeys([]);
    };

    const onSelectRow = (checked, messageKey) => {
        if (checked) {
            setSelectedMessageKeys([messageKey, ...selectedMessageKeys]);
        } else {
            setSelectedMessageKeys(selectedMessageKeys.filter(m => m !== messageKey));
        }
    };

    const onSelectAllRows = checked => {
        if (checked) {
            setSelectedMessageKeys(messages.map(message => message.key));
        } else {
            setSelectedMessageKeys([]);
        }
    };

    const handleViewMessage = (message) => {
        setCurrentMessage(message);
    };

    const onOpenComposeDialog = () => {
        setComposeDialogOpen(true);
    };

    const onCloseComposeDialog = () => {
        setComposeDialogOpen(false);
    };

    const onMessageSent = () => {
        updateReload(true);
    };

    return (
        <div className={'ch-messages-wrapper'}>
            {user.subscriberType !== 'STUDENT' &&
                <>
                    <Tabs defaultActiveKey={TABS.INBOX.key} onChange={handleTabChange} variant={"standard"} destroyOnHidden={true}>
                        <Tab tab={<>
                            <Inbox className={'margin-r--m'}/>
                            {TABS.INBOX.name}
                        </>} key={TABS.INBOX.key} hash={TABS.INBOX.hash}
                             children={<>
                                 <TableAntD
                                     scroll={{x: 'max-content'}}
                                     size={'small'}
                                     reload={reload}
                                     columnData={TABS.INBOX.cols}
                                     pagingData={{fetchFunction: getUserMessages, params: {mailbox: TABS.INBOX.key, pageSize: 10, currentPage: 1}}}
                                     noDataMessage={`You have no messages in your ${TABS.INBOX.name}`}
                                     rowClassName={(record) => (record.read ? '' : 'unread')}
                                 />
                                 <ButtonsGroup>
                                     <Button onClick={onOpenComposeDialog} aria-label={'Compose message'}>Compose</Button>
                                     <PopConfirm title={<span>Delete selected messages?</span>}
                                                 placement={'top'}
                                                 okText={'Delete'}
                                                 onConfirm={deleteSelectedMessages}>
                                         <Button type={'outline'} disabled={selectedMessageKeys.length === 0} aria-label={'Delete selected messages'}>Delete</Button>
                                     </PopConfirm>
                                     <PopConfirm title={<span>Delete all messages?</span>}
                                                 placement={'top'}
                                                 okText={'Delete All'}
                                                 onConfirm={deleteAllMessages}>
                                         <Button type={'outline'} disabled={isDeleteAllDisabled} aria-label={'Delete all messages'}>Delete All</Button>
                                     </PopConfirm>
                                 </ButtonsGroup>
                             </>}
                        />
                        <Tab tab={<>
                            <Outbox className={'margin-r--m'}/>
                            {TABS.SENT.name}
                        </>}
                             key={TABS.SENT.key} hash={TABS.SENT.hash}
                             children={<>
                                 <TableAntD
                                     scroll={{x: 'max-content'}}
                                     size={'small'}
                                     reload={reload}
                                     columnData={TABS.SENT.cols}
                                     pagingData={{fetchFunction: getUserMessages, params: {mailbox: TABS.SENT.key, pageSize: 10, currentPage: 1}}}
                                     noDataMessage={`You have no messages in your ${TABS.SENT.name}`}
                                 />
                                 <ButtonsGroup>
                                     <Button onClick={onOpenComposeDialog} aria-label={'Compose message'}>Compose</Button>
                                 </ButtonsGroup>
                             </>}
                        />
                    </Tabs>
                </>
            }
            {user.subscriberType === 'STUDENT' &&
                <>
                    <h2 className={'flexAlignCentre'}><Inbox className={'margin-r--m'} /> {TABS.INBOX.name}</h2>
                    <TableAntD
                        scroll={{x: 'max-content'}}
                        size={'small'}
                        reload={reload}
                        columnData={TABS.INBOX.cols}
                        pagingData={{fetchFunction: getUserMessages, params: {mailbox: TABS.INBOX.key, pageSize: 10, currentPage: 1}}}
                        noDataMessage={'You have no messages in your ' + TABS.INBOX.name}
                        rowClassName={(record) => (record.read ? '' : 'unread')}
                    />
                    <ButtonsGroup>
                        <PopConfirm title={<span>Delete selected messages?</span>}
                                    placement={'top'}
                                    okText={'Delete'}
                                    onConfirm={deleteSelectedMessages}>
                            <Button disabled={selectedMessageKeys.length === 0} aria-label={'Delete selected messages'}>Delete</Button>
                        </PopConfirm>
                        <PopConfirm title={<span>Delete all messages?</span>}
                                    placement={'top'}
                                    okText={'Delete All'}
                                    onConfirm={deleteAllMessages}>
                            <Button type={'outline'} disabled={isDeleteAllDisabled} aria-label={'Delete all messages'}>Delete All</Button>
                        </PopConfirm>
                    </ButtonsGroup>
                </>
            }

            <ComposeMessageDialog
                isOpen={isComposeDialogOpen}
                onClose={onCloseComposeDialog}
                onMessageSent={onMessageSent}
            />

            <GenericDialog
                title={currentMessage ? currentMessage.subject : 'View message'}
                open={isReadMessageDialogOpen}
                onClose={updateReload}
                buttons={[{name: 'Close'}]}
                dialogWidth={820}
            >
                {currentMessage &&
                    <>
                        <div className={'ch-text-b ch-text-large'}>{currentMessage.from}</div>
                        <div>To {currentMessage.to}</div>
                        <Divider className={'margin-t--m margin-b--s'} />
                        <div dangerouslySetInnerHTML={{__html: currentMessage.body}} />
                    </>
                }
            </GenericDialog>
        </div>
    );
};

export default Messages;